// Shared formatting helpers and chart colors for the activations dashboard.

// Chart colors. Kept apart from the MUI theme so the series stay distinguishable
// whatever your primary color is. Activations / new installs / released differ in
// lightness as well as hue, so they still read for color-blind users.
export const chartColors = {
  activations: '#00897B', // teal
  newInstalls: '#1E3A8A', // dark blue
  refreshes: '#80CBC4',   // light teal
  released: '#E65100',    // orange
  failed: '#B71C1C',      // red
  manual: '#1E3A8A',
  offline: '#E65100',
};

const intFmt = new Intl.NumberFormat('en-US');
export const fmtInt = (n) => intFmt.format(n ?? 0);

export const fmtPct = (ratio, digits = 1) => `${((ratio ?? 0) * 100).toFixed(digits)}%`;

/** Percentage change between periods. Returns null when there is no previous value to compare. */
export function pctChange(current, previous) {
  if (!previous) return null;
  return (current - previous) / Math.abs(previous);
}

// The API returns MariaDB DATETIME strings ("2026-10-11 16:52:03") in server time.
// They are parsed as local time; if the server stores UTC, append "Z" here instead.
export function parseServerDate(s) {
  if (!s) return null;
  return new Date(s.replace(' ', 'T'));
}

const dateTimeFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
export const fmtDateTime = (s) => {
  const d = parseServerDate(s);
  return d ? dateTimeFmt.format(d) : '—';
};

const shortDateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
/** For "YYYY-MM-DD" strings (chart buckets). */
export const fmtShortDate = (ymd) => shortDateFmt.format(fromIsoDate(ymd));

export function fmtDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  const minute = 60, hour = 3600, day = 86400;
  if (seconds < hour) return `${Math.max(1, Math.round(seconds / minute))} min`;
  if (seconds < day) return plural(Math.round(seconds / hour), 'hour');
  if (seconds < 60 * day) return plural(Math.round(seconds / day), 'day');
  if (seconds < 730 * day) return plural(Math.round(seconds / (30.44 * day)), 'month');
  return plural(Math.round(seconds / (365.25 * day)), 'year');
}
const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** Local date → "YYYY-MM-DD" (not toISOString, which would shift to UTC). */
export function toIsoDate(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
export function fromIsoDate(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Friendly names for ActivationType values. */
export const methodLabels = {
  XmlActivationService: 'Online',
  ManualRequest: 'Manual request',
  OfflineActivation: 'Offline',
};
export const methodColor = (type) =>
  ({ XmlActivationService: chartColors.activations, ManualRequest: chartColors.manual, OfflineActivation: chartColors.offline }[type] ?? '#9AA4B2');
